/**
 * "Benchmarks" panel (lane BEN — live benchmarking per fly). Binds to
 * `index.json.refs.benchmarks` (`RoleSnapshot.benchmarks()`): the live W(t)
 * against equal-weight buy-and-hold, the 60% core_only reference, monthly
 * buy-and-hold and cash since the episode's genesis (chart + legend with a
 * crosshair readout), a key-stats table, the regime strip (the organism's
 * own detector and the buy-and-hold rule), the rolling 30-day gate and the
 * consistency scorecard — each with an explicit "insufficient data" state
 * until enough days exist. Mirrors `SizingPanel.ts`'s poller/empty-state
 * structure and `PortfolioPanel.ts`'s lazily-imported chart chunk.
 */

import type { RoleSnapshot } from "../data/snapshotIndex";
import type { BenchmarkCheck, BenchmarkSeriesName, BenchmarksDocument } from "../types/incremental";
import type { BenchmarkChart } from "../charts/BenchmarkChart";
import { renderNotPublishedYet } from "./NotPublishedYet";
import {
  SERIES_COLORS,
  SERIES_DASHED,
  SERIES_LABELS,
  SERIES_ORDER,
  SERIES_TOOLTIPS,
  benchmarksFingerprint,
  buildBenchmarksPanelState,
  checkLabel,
  checkMark,
  regimeClass,
  regimeRuns,
  regimeText,
  verdictText,
} from "./benchmarksView";
import { buildForecastSkill } from "./ForecastSkillBlock";
import { MarketRelativeBlock } from "./MarketRelativeBlock";
import { Poller, freshnessBadgeLabel } from "../data/polling";
import { isNotPublishedYet } from "../data/fetchJson";
import { config } from "../config";
import { attachTooltip } from "../ui/tooltip";
import {
  formatDecimal,
  formatMagnitudePercent,
  formatMoney,
  formatPercent,
  formatSignedPercent,
  toneClass,
  type ToneText,
} from "../format/number";

export class BenchmarksPanel {
  private poller: Poller<BenchmarksDocument | null> | null = null;
  private freshnessEl: HTMLSpanElement | null = null;
  private chart: BenchmarkChart | null = null;
  private chartWrap: HTMLDivElement | null = null;
  private legendValues = new Map<BenchmarkSeriesName, HTMLSpanElement>();
  private latest: BenchmarksDocument | null = null;
  /** OBS-REL: the "edge vs a same-size market basket" block (first in the panel). */
  private relative: MarketRelativeBlock | null = null;
  private disposed = false;

  constructor(
    private readonly container: HTMLElement,
    private readonly role: RoleSnapshot,
  ) {}

  async mount(): Promise<void> {
    this.container.innerHTML = "";
    const initial = await this.safeFetch();
    // Torn down while the first fetch/render was in flight: never start a poller that
    // nothing will stop (review round 1).
    if (this.disposed) return;
    await this.render(initial);
    if (this.disposed) return;
    this.poller = new Poller<BenchmarksDocument | null>(
      async () => {
        const doc = await this.safeFetch();
        return { key: benchmarksFingerprint(doc), value: doc };
      },
      (snapshot) => {
        // Only a real result re-renders: a transient poll error keeps what is on screen
        // (the poller's value is still null until its own first success).
        if (snapshot.status === "ok" || snapshot.status === "not-published-yet") {
          void this.render(snapshot.value);
        }
        if (this.freshnessEl) {
          this.freshnessEl.textContent = freshnessBadgeLabel(
            snapshot.status,
            snapshot.lastSuccessAt,
          );
        }
      },
      { intervalMs: config.pollIntervalMs, backoffMaxMs: config.pollBackoffMaxMs },
      isNotPublishedYet,
    );
    this.poller.start();
  }

  private async safeFetch(): Promise<BenchmarksDocument | null> {
    try {
      return await this.role.benchmarks();
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  private async render(doc: BenchmarksDocument | null): Promise<void> {
    const state = buildBenchmarksPanelState(doc);
    this.chart?.dispose();
    this.chart = null;
    this.relative?.dispose();
    this.relative = null;
    this.container.innerHTML = "";
    this.legendValues.clear();
    // Any in-flight ready render's chart construction sees these and bails out.
    this.latest = null;
    this.chartWrap = null;
    if (this.disposed) return;
    if (state.kind === "empty") {
      this.container.appendChild(
        renderNotPublishedYet(
          "Benchmarks",
          "No benchmarks object is published for this organism yet — it appears with the " +
            "first publish that folds a W(t) sample of the current episode (a stopped " +
            "organism's fresh episode has none until its first wake).",
        ),
      );
      return;
    }
    if (state.kind === "pending") {
      this.container.appendChild(renderNotPublishedYet("Benchmarks", state.message));
      // The forecast scorecard does not wait for a W(t) genesis.
      const skill = doc ? buildForecastSkill(doc) : null;
      if (skill) this.container.appendChild(skill);
      return;
    }
    const live = state.doc;
    this.latest = live;
    const chartModulePromise = import("../charts/BenchmarkChart");
    chartModulePromise.catch(() => {});

    const root = document.createElement("div");
    root.className = "obs-bench";
    root.appendChild(this.buildHeader(live));
    if (live.insufficient_data) root.appendChild(this.buildInsufficientBanner(live));
    // OBS-REL: the market-relative view first — the dollar chart below tracks the market.
    const relative = new MarketRelativeBlock(live);
    this.relative = relative;
    root.appendChild(relative.element);
    const coarse = live.data_quality?.coarse_quote_symbols ?? [];
    if (coarse.length > 0) {
      const note = document.createElement("div");
      note.className = "obs-panel-hint";
      note.textContent =
        `Cent-rounded quotes for ${coarse.join(", ")}: their benchmark marks carry up to half a ` +
        "cent of rounding and their fills are left out of spread paid (full-precision quotes " +
        "start with the organism's next restart).";
      root.appendChild(note);
    }
    root.appendChild(this.buildLegend(live));
    const chartWrap = document.createElement("div");
    chartWrap.className = "obs-bench-chart-wrap";
    this.chartWrap = chartWrap;
    root.appendChild(chartWrap);
    root.appendChild(this.buildStatsTable(live));
    root.appendChild(this.buildRelativeLine(live));
    root.appendChild(this.buildRegimeStrip(live));
    const verdicts = document.createElement("div");
    verdicts.className = "obs-bench-verdicts";
    verdicts.appendChild(this.buildGate(live));
    verdicts.appendChild(this.buildScorecard(live));
    root.appendChild(verdicts);
    const skill = buildForecastSkill(live);
    if (skill) root.appendChild(skill);
    this.container.appendChild(root);
    void relative.mountChart();

    const { BenchmarkChart } = await chartModulePromise;
    // Torn down, or re-rendered by a newer poll, while the chunk was in flight.
    if (this.disposed || this.latest !== live || this.chartWrap !== chartWrap) return;
    this.chart = new BenchmarkChart(chartWrap, (values) => this.updateLegend(values, live));
    this.chart.setDocument(live);
  }

  private buildHeader(doc: BenchmarksDocument): HTMLDivElement {
    const header = document.createElement("div");
    header.className = "obs-sizing-header";
    const since = document.createElement("span");
    const genesis = doc.genesis;
    const start = genesis?.event_time ? genesis.event_time.slice(0, 16).replace("T", " ") : "–";
    since.textContent =
      `since genesis ${start}Z · ${formatMoney(genesis?.opening_capital ?? null)} · ` +
      `${doc.days_observed ?? 0} counted day${doc.days_observed === 1 ? "" : "s"} · ` +
      `${genesis?.universe.length ?? 0} symbols`;
    attachTooltip(
      since,
      "Every series starts at the episode's genesis — its first event " +
        `(${genesis?.time_source ?? "–"}) — with the ledger's opening capital ` +
        `(${genesis?.source ?? "–"}). Universe: ${(genesis?.universe ?? []).join(", ") || "–"}. ` +
        "Counted days: every " +
        "UTC day for the female; regular-session days only for the male.",
    );
    header.appendChild(since);
    const freshness = document.createElement("span");
    freshness.className = "obs-freshness";
    freshness.textContent = `seq ${doc.as_of_seq}`;
    this.freshnessEl = freshness;
    header.appendChild(freshness);
    return header;
  }

  private buildInsufficientBanner(doc: BenchmarksDocument): HTMLDivElement {
    const banner = document.createElement("div");
    banner.className = "obs-bench-banner";
    banner.textContent =
      `Insufficient data: ${doc.days_observed ?? 0} of ${doc.min_days ?? 20} days. ` +
      "The gate and scorecard below are provisional until then — a few days of returns say " +
      "little about skill.";
    return banner;
  }

  private buildLegend(doc: BenchmarksDocument): HTMLDivElement {
    const legend = document.createElement("div");
    legend.className = "obs-bench-legend";
    for (const name of SERIES_ORDER) {
      const item = document.createElement("span");
      item.className = "obs-bench-legend-item";
      const swatch = document.createElement("span");
      swatch.className = "obs-bench-legend-swatch";
      if (SERIES_DASHED.has(name)) swatch.classList.add("obs-bench-legend-swatch-dashed");
      swatch.style.setProperty("--obs-bench-color", SERIES_COLORS[name]);
      item.appendChild(swatch);
      item.appendChild(document.createTextNode(SERIES_LABELS[name]));
      const value = document.createElement("span");
      value.className = "obs-bench-legend-value";
      value.textContent = formatMoney(doc.stats?.[name].final_value ?? null);
      this.legendValues.set(name, value);
      item.appendChild(value);
      attachTooltip(item, SERIES_TOOLTIPS[name]);
      legend.appendChild(item);
    }
    return legend;
  }

  private updateLegend(
    values: Partial<Record<BenchmarkSeriesName, number>> | null,
    doc: BenchmarksDocument,
  ): void {
    for (const [name, el] of this.legendValues) {
      const value = values ? values[name] : doc.stats?.[name].final_value;
      el.textContent = formatMoney(value ?? null);
    }
  }

  private buildStatsTable(doc: BenchmarksDocument): HTMLTableElement {
    const table = document.createElement("table");
    table.className = "obs-bench-table";
    const head = document.createElement("tr");
    const columns: [string, string][] = [
      ["Series", ""],
      ["Return", "Since genesis, after all costs and the tariff."],
      ["Live − this", "The organism's return minus this benchmark's (excess return)."],
      ["Last day", "The latest counted day's close-to-close return."],
      ["Sharpe", "Daily returns, mean/std × √252 (male) or √365 (female) — the gate's Sharpe."],
      ["Max DD", "Largest peak-to-trough fall over every sample since genesis."],
      ["Vol", "Annualized realized volatility of daily returns."],
    ];
    for (const [label, tip] of columns) {
      const th = document.createElement("th");
      th.textContent = label;
      if (tip) attachTooltip(th, tip);
      head.appendChild(th);
    }
    table.appendChild(head);
    for (const name of SERIES_ORDER) {
      const stats = doc.stats?.[name];
      const row = document.createElement("tr");
      if (name === "live") row.className = "obs-bench-row-live";
      const label = document.createElement("td");
      const swatch = document.createElement("span");
      swatch.className = "obs-bench-legend-swatch";
      if (SERIES_DASHED.has(name)) swatch.classList.add("obs-bench-legend-swatch-dashed");
      swatch.style.setProperty("--obs-bench-color", SERIES_COLORS[name]);
      label.appendChild(swatch);
      label.appendChild(document.createTextNode(SERIES_LABELS[name]));
      row.appendChild(label);
      row.appendChild(this.toneCell(formatSignedPercent(stats?.total_return)));
      row.appendChild(
        name === "live"
          ? this.textCell("–")
          : this.toneCell(formatSignedPercent(doc.relative?.[name].excess_return)),
      );
      row.appendChild(this.toneCell(formatSignedPercent(stats?.daily_return)));
      row.appendChild(this.textCell(formatDecimal(stats?.sharpe ?? null, 2)));
      row.appendChild(this.toneCell(formatMagnitudePercent(stats?.max_drawdown)));
      row.appendChild(this.textCell(formatPercent(stats?.realized_vol ?? null, 1)));
      table.appendChild(row);
    }
    return table;
  }

  private buildRelativeLine(doc: BenchmarksDocument): HTMLDivElement {
    const line = document.createElement("div");
    line.className = "obs-bench-relative";
    const bh = doc.relative?.buy_and_hold;
    const parts: [string, string, string][] = [
      [
        "Up / down capture vs B&H",
        `${formatDecimal(bh?.upside_capture ?? null, 2)} / ${formatDecimal(bh?.downside_capture ?? null, 2)}`,
        "Share of buy-and-hold's up-day (down-day) moves the organism captured. Under 1.0 on the down side = it lost less on bad days.",
      ],
      [
        "Tracking error",
        formatPercent(bh?.tracking_error ?? null, 1),
        "Annualized std of the daily return difference vs buy-and-hold.",
      ],
      [
        "Information ratio",
        formatDecimal(bh?.information_ratio ?? null, 2),
        "Annualized mean daily excess vs buy-and-hold divided by the tracking error.",
      ],
      [
        "Tilt contribution",
        doc.tilt?.active ? formatSignedPercent(doc.tilt.contribution).text : "0 (tilt off)",
        "Live minus the core_only reference once the brain's tilt is moving the book; exactly 0 while the tilt is off. " +
          `Live − core_only right now: ${formatSignedPercent(doc.tilt?.implementation_gap).text} (execution, not skill, while the tilt is off).`,
      ],
      [
        "Costs",
        `${formatMoney(doc.costs?.fees_paid ?? null)} fees · ${formatMoney(doc.costs?.spread_paid ?? null)} spread`,
        `Venue fees and spread paid since genesis (spread = |fill − sane mid| × qty). Turnover ` +
          `${formatDecimal(doc.costs?.turnover_per_30d ?? null, 2)}×/30d; fees + spread ` +
          `${formatPercent(doc.costs?.fees_spread_per_30d ?? null, 3)} of opening capital per 30d.`,
      ],
    ];
    for (const [label, value, tip] of parts) {
      const item = document.createElement("span");
      item.className = "obs-bench-relative-item";
      const labelEl = document.createElement("span");
      labelEl.className = "obs-stat-label";
      labelEl.textContent = label;
      const valueEl = document.createElement("span");
      valueEl.className = "obs-bench-relative-value";
      valueEl.textContent = value;
      item.appendChild(labelEl);
      item.appendChild(valueEl);
      attachTooltip(item, tip);
      line.appendChild(item);
    }
    return line;
  }

  private buildRegimeStrip(doc: BenchmarksDocument): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.className = "obs-bench-regimes";
    const days = doc.regimes?.days ?? [];
    const rows: ["organism" | "rule", string, string][] = [
      [
        "organism",
        "Organism regime",
        "The organism's own regime detector: argmax state of the posterior recorded at or before each day's first sample (S0–S3 are unnamed learned states).",
      ],
      [
        "rule",
        "Market rule",
        "A documented rule on the buy-and-hold index from the 20 counted days BEFORE each day: high vol, trend up/down, or chop (warmup below 5 days).",
      ],
    ];
    for (const [key, title, tip] of rows) {
      const row = document.createElement("div");
      row.className = "obs-bench-regime-row";
      const label = document.createElement("span");
      label.className = "obs-stat-label";
      label.textContent = title;
      attachTooltip(label, tip);
      row.appendChild(label);
      const strip = document.createElement("div");
      strip.className = "obs-bench-regime-strip";
      if (days.length === 0) {
        strip.classList.add("obs-bench-regime-empty");
        strip.textContent = "no counted day yet";
      }
      for (const run of regimeRuns(days, key)) {
        const segment = document.createElement("span");
        segment.className = `obs-bench-regime-seg ${regimeClass(run.label)}`;
        segment.style.flexGrow = String(run.days);
        segment.textContent = run.days >= 3 ? regimeText(run.label) : "";
        const segmentStats = doc.regimes?.[key][run.label];
        attachTooltip(
          segment,
          `${regimeText(run.label)}: ${run.start} → ${run.end} (${run.days} day${run.days === 1 ? "" : "s"}). ` +
            (segmentStats
              ? `Across all ${segmentStats.days} such days: live ${formatSignedPercent(segmentStats.live_return).text}, ` +
                `B&H ${formatSignedPercent(segmentStats.buy_and_hold_return).text}, excess ` +
                `${formatSignedPercent(segmentStats.excess_return).text}.`
              : ""),
        );
        strip.appendChild(segment);
      }
      row.appendChild(strip);
      wrap.appendChild(row);
    }
    return wrap;
  }

  private buildGate(doc: BenchmarksDocument): HTMLDivElement {
    const gate = doc.gate;
    const verdict = verdictText(gate?.status, gate?.counted_days_since_genesis, gate?.min_days);
    const window =
      gate?.window_start && gate.window_end ? `${gate.window_start} → ${gate.window_end}` : "–";
    return this.verdictBlock(
      `Rolling ${gate?.window_days ?? 30}-day gate`,
      verdict,
      gate?.checks ?? [],
      gate?.version === "v2"
        ? `The go-live criteria of docs/BACKTEST_GATE.md (gate v2): cash, drawdown and cost on the trailing 30 calendar days (${window}); the risk-adjusted criterion (Jensen alpha over T-bills and convexity vs buy & hold) on every day since genesis, judged from ${gate.gate4_v2?.min_days ?? 63} counted days. Judged from ${gate.min_days ?? 20} counted days; shown provisionally before that.`
        : `The four go-live criteria of docs/BACKTEST_GATE.md evaluated on the live series over the trailing 30 calendar days (${window}). Judged from ${gate?.min_days ?? 20} counted days; shown provisionally before that.`,
    );
  }

  private buildScorecard(doc: BenchmarksDocument): HTMLDivElement {
    const card = doc.scorecard;
    const verdict = verdictText(card?.status, doc.days_observed, doc.min_days);
    return this.verdictBlock(
      `Consistency scorecard${card?.source === "placeholder" ? " (placeholder)" : ""}`,
      verdict,
      card?.checks ?? [],
      card?.note ?? "Regime / consistency scorecard.",
    );
  }

  private verdictBlock(
    title: string,
    verdict: { text: string; className: string },
    checks: readonly BenchmarkCheck[],
    tip: string,
  ): HTMLDivElement {
    const block = document.createElement("div");
    block.className = "obs-bench-verdict";
    const head = document.createElement("div");
    head.className = "obs-bench-verdict-head";
    const titleEl = document.createElement("span");
    titleEl.textContent = title;
    attachTooltip(titleEl, tip);
    const pill = document.createElement("span");
    pill.className = `obs-bench-verdict-pill ${verdict.className}`;
    pill.textContent = verdict.text;
    head.appendChild(titleEl);
    head.appendChild(pill);
    block.appendChild(head);
    const list = document.createElement("ul");
    list.className = "obs-bench-checks";
    for (const check of checks) {
      const item = document.createElement("li");
      const mark = checkMark(check.passed);
      const markEl = document.createElement("span");
      markEl.className = `obs-bench-check-mark ${mark.className}`;
      markEl.textContent = mark.text;
      item.appendChild(markEl);
      item.appendChild(document.createTextNode(checkLabel(check.name)));
      attachTooltip(item, check.detail);
      list.appendChild(item);
    }
    block.appendChild(list);
    return block;
  }

  private toneCell(value: ToneText): HTMLTableCellElement {
    const cell = document.createElement("td");
    cell.textContent = value.text;
    cell.className = toneClass(value.tone);
    return cell;
  }

  private textCell(text: string): HTMLTableCellElement {
    const cell = document.createElement("td");
    cell.textContent = text;
    return cell;
  }

  dispose(): void {
    this.disposed = true;
    this.poller?.stop();
    this.chart?.dispose();
    this.chart = null;
    this.relative?.dispose();
    this.relative = null;
  }
}
